import { updateStoredMemberHash } from '@common/utils/memberHash/updateStoredMemberHash'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import type { Member } from '@vista-digital/ocapi-types/v1'
import {
    getMember,
    isMemberSignedIn,
    loadMember,
    signMemberOut,
    temporaryLoyaltyDomainStore,
} from '@vista-digital/occ-domain'
import { LoadingState } from '@vista-digital/utilities'
import { useEffect, useState } from 'react'

export const useLoadMember = () => {
    const logger = useLogger('MemberStatus')

    const [memberName, setMemberName] = useState('')

    const [loadingMember, setLoadingMember] = useState(true)
    const [loadingError, setLoadingError] = useState(false)

    const [loadMemberWithDelay, setLoadMemberWithDelay] = useState<NodeJS.Timeout | null>(null)
    const [loadMemberWhenDataExpires, setLoadMemberWhenDataExpires] = useState<NodeJS.Timeout | null>(null)

    const signedIn = isMemberSignedIn()
    const member = getMember()

    useEffect(() => {
        logger.debug('Status updated', { signedIn, member })

        if (!signedIn) {
            setLoadingMember(false)
            return
        }

        setLoadingMember(true)

        if (!member) {
            logger.debug('setting setLoadMemberWithDelay')

            // This is more of a fallback, the member should get loaded in the MemberContext and then the getMember should have the correct value.
            setLoadMemberWithDelay(
                setTimeout(() => {
                    logger.debug('loading member from delay')
                    manualLoadMember()
                }, 5000)
            )

            return
        }

        if (loadMemberWithDelay) {
            logger.debug('clearing setLoadMemberWithDelay')
            clearTimeout(loadMemberWithDelay)
        }

        onMemberLoaded(member)
        setLoadingMember(false)

        // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when signedIn or member changes
    }, [signedIn, member])

    const onMemberLoaded = (loadedMember: Member) => {
        setMemberName(`${loadedMember.personalDetails.name.givenName} ${loadedMember.personalDetails.name.familyName}`)

        updateStoredMemberHash(loadedMember)

        // Set a timeout for when member data (from temporaryLoyaltyDomainStore) expires to force reload it
        // This fixes 14755 and doesn't require the fix code for 13035
        if (!loadedMember) return

        if (loadMemberWhenDataExpires) {
            clearTimeout(loadMemberWhenDataExpires)
            setLoadMemberWhenDataExpires(null)
        }

        if (temporaryLoyaltyDomainStore.data.member.loadingState === LoadingState.Success) {
            const expiryDate = new Date(temporaryLoyaltyDomainStore.data.member.expiresAt)
            const timeout = expiryDate.getTime() - new Date().getTime() - 30000 // Reload 30s before it expires
            setLoadMemberWhenDataExpires(
                setTimeout(
                    () => {
                        logger.debug('Memory member data about to expire, reloading...')
                        manualLoadMember(true)
                    },
                    timeout > 0 ? timeout : 100
                )
            )
        }
    }

    const manualLoadMember = (reload: boolean = false) => {
        loadMember(reload)
            .then(onMemberLoaded, onLoadingError)
            .catch(onLoadingError)
            .finally(() => {
                setLoadingMember(false)
            })
    }

    const onLoadingError = async err => {
        setLoadingError(true)
        logger.error('Failed to load member status, maybe sign in again?', err)
        await signMemberOut()
    }

    return { signedIn, memberName, loadingMember, loadingError }
}
